import { PopoverPosition as BlueprintPopoverPosition } from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import { Export, OverflowMenuHorizontal, Settings } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import {
  Button,
  MenuItem,
  MenuItems,
  Tooltip,
} from 'vet-bones/design-system/components';

import { exportProject } from 'src/actions/projects/action-creators/exportProject';
import { TEST_IDS } from 'src/constants/testIds';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectSelector } from 'src/selectors/project';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { confirmAcceptInvalidMappings } from 'src/utils/export/confirmAcceptInvalidMappings';
import { getInvalidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectNavbar/projectNavbar.module.scss';

export const ProjectNavbarActions: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const labelsByIri = useReduxSelector(projectLabelsByIriSelector(projectId));
  const isDraftDiagram = useReduxSelector(isDraftDiagramSelector(projectId));

  const connection = useDefaultPortalConnection();

  const getUserConfirmationForInvalidMappings = async () => {
    if (!project) {
      return false;
    }
    const invalidMappingsByResource = getInvalidMappingsByResource(
      project.diagram.model
    );
    const shouldPublish = await confirmAcceptInvalidMappings({
      invalidMappingsByResource,
      fileUploadsById: project.fileUploadsById,
      virtualGraphsById: project.virtualGraphsById,
      labelsByIri,
    });
    return shouldPublish;
  };

  const disabled = !project || isDraftDiagram;

  const handleSettings = () => {
    if (disabled) {
      return;
    }
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: true }));
  };

  const handleExport = async () => {
    if (disabled) {
      return;
    }
    const shouldExport = await getUserConfirmationForInvalidMappings();
    if (!shouldExport) {
      return;
    }
    dispatch(exportProject(projectId, connection));
  };

  const handlePublish = async () => {
    if (disabled) {
      return;
    }
    const shouldExport = await getUserConfirmationForInvalidMappings();
    if (!shouldExport) {
      return;
    }
    dispatch(uiSlice.actions.toggledPublishProjectDrawer({ isOpen: true }));
  };

  return (
    <>
      <Popover2
        content={
          <MenuItems className={styles.projectNavbarActionsMenuItems}>
            <Tooltip
              description={
                isDraftDiagram
                  ? copy.components.voicebox.disabledFor.export
                  : copy.components.project.navbar.exportHover
              }
              disabled={!disabled}
              position={BlueprintPopoverPosition.LEFT}
            >
              <MenuItem
                disabled={disabled}
                leftIcon={Export}
                onClick={handleExport}
                testId={TEST_IDS.projectNavbar.export.zip}
                text={copy.components.project.navbar.export}
              />
            </Tooltip>
            <Tooltip
              description={copy.components.voicebox.disabledFor.settings}
              disabled={!disabled}
              position={BlueprintPopoverPosition.LEFT}
            >
              <MenuItem
                disabled={disabled}
                leftIcon={Settings}
                onClick={handleSettings}
                testId={TEST_IDS.projectNavbar.settings}
                text={copy.components.project.navbar.settings}
              />
            </Tooltip>
          </MenuItems>
        }
        minimal
        popoverClassName={classNames(
          'vb-menu-popover',
          `vb-popover-position--${BlueprintPopoverPosition.BOTTOM_RIGHT}`
        )}
        position={BlueprintPopoverPosition.BOTTOM_RIGHT}
      >
        <Button
          leftIcon={OverflowMenuHorizontal}
          size="md"
          testId={TEST_IDS.projectNavbar.more}
          variant="secondary-transparent-gray"
        />
      </Popover2>

      <Tooltip
        description={copy.components.voicebox.disabledFor.publish}
        disabled={!disabled}
        position={BlueprintPopoverPosition.TOP_LEFT}
      >
        <Button
          disabled={disabled}
          onClick={handlePublish}
          size="md"
          testId={TEST_IDS.projectNavbar.export.publish}
          variant="primary-accent"
        >
          {copy.components.project.navbar.publish}
        </Button>
      </Tooltip>
    </>
  );
};
